/**
 * 响应式样式指令
 * 自动根据设备类型应用不同的类名
 * 
 * 使用方法：
 * <div v-responsive="{ mobile: 'p-2', tablet: 'p-4', desktop: 'p-6' }">内容</div>
 * <div v-responsive:padding="{ mobile: 'p-2', tablet: 'p-4', desktop: 'p-6' }">内容</div>
 */

import { getDeviceType } from '@/utils/viewport'
import { debounce } from '@/utils/performance'

// 存储所有绑定的元素和配置
const bindings = new WeakMap()

// 全局设备类型缓存
let currentDeviceType = getDeviceType()

// 更新所有绑定元素的类名
function updateAllBindings() {
  const newDeviceType = getDeviceType()
  
  // 只有设备类型真正改变时才更新
  if (newDeviceType === currentDeviceType) return
  
  currentDeviceType = newDeviceType
  
  // 触发所有元素更新
  document.querySelectorAll('[data-responsive]').forEach(el => {
    const binding = bindings.get(el)
    if (binding) {
      updateElement(el, binding)
    }
  })
}

// 防抖的全局更新函数
const debouncedUpdate = debounce(updateAllBindings, 150)

// 监听窗口大小变化
let isListening = false
function startListening() {
  if (isListening) return
  window.addEventListener('resize', debouncedUpdate, { passive: true })
  isListening = true
}

// 更新单个元素的类名
function updateElement(el, binding) {
  const { value, oldClasses } = binding
  
  // 移除旧的类名
  if (oldClasses) {
    el.classList.remove(...oldClasses.split(' ').filter(Boolean))
  }
  
  // 获取当前设备类型对应的类名
  let newClasses = ''
  if (typeof value === 'string') {
    newClasses = value
  } else if (typeof value === 'object') {
    newClasses = value[currentDeviceType] || value.desktop || ''
  }
  
  // 添加新的类名
  if (newClasses) {
    el.classList.add(...newClasses.split(' ').filter(Boolean))
  }
  
  // 保存当前类名
  binding.oldClasses = newClasses
}

export default {
  mounted(el, binding) {
    // 标记元素
    el.setAttribute('data-responsive', '')
    
    // 保存绑定信息
    bindings.set(el, {
      value: binding.value,
      oldClasses: ''
    })
    
    // 初始化类名
    updateElement(el, bindings.get(el))
    
    // 开始监听（只需要一次）
    startListening()
  },
  
  updated(el, binding) {
    const bindingData = bindings.get(el)
    if (bindingData) {
      bindingData.value = binding.value
      updateElement(el, bindingData)
    }
  },
  
  beforeUnmount(el) {
    // 清理
    el.removeAttribute('data-responsive')
    bindings.delete(el)
  }
}

